import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { TimelineEvent } from '../components/subscription-detail/TimelineEvent';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Button } from '../components/SubscriptionAppButton';
import { Checkbox } from '../components/Checkbox';
import { Input } from '../components/SubscriptionAppInput';
import { Toast } from '../components/Toast';
import { useAppMode } from '../contexts/AppModeContext';
import { BillingStatusBanner, BillingStatePreviewControl, billingPreviewProps, BillingPreviewState } from '../components/BillingStatusBanner';
import { Settings, Award, Gift, User, ShoppingBag, MapPin, ChevronRight, X, ChevronDown, AlertTriangle, CheckCircle2, Zap, Trash2, BarChart3 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
// Mock Data
const pointsActivityData = [{
  name: 'Mon',
  earned: 4000,
  redeemed: 2400,
  expired: 400
}, {
  name: 'Tue',
  earned: 3000,
  redeemed: 1398,
  expired: 200
}, {
  name: 'Wed',
  earned: 2000,
  redeemed: 9800,
  expired: 200
}, {
  name: 'Thu',
  earned: 2780,
  redeemed: 3908,
  expired: 300
}, {
  name: 'Fri',
  earned: 1890,
  redeemed: 4800,
  expired: 100
}, {
  name: 'Sat',
  earned: 2390,
  redeemed: 3800,
  expired: 200
}, {
  name: 'Sun',
  earned: 3490,
  redeemed: 4300,
  expired: 100
}];
const tierDistributionData = [{
  name: 'Enthusiast',
  value: 62,
  color: '#0EA5E9'
}, {
  name: 'Connoisseur',
  value: 24,
  color: '#6F42C1'
}, {
  name: 'Sommelier',
  value: 11,
  color: '#F59E0B'
}, {
  name: 'Vintner',
  value: 3,
  color: '#10B981'
} // Ocean Success
];
const recentActivity = [{
  id: 1,
  type: 'earned',
  user: 'Sarah Johnson',
  title: 'Points Earned',
  description: 'Sarah Johnson earned 150 points from Order #4521',
  time: '2 hours ago',
  icon: ShoppingBag,
  iconColor: '#10B981'
}, {
  id: 2,
  type: 'redeemed',
  user: 'James Chen',
  title: 'Reward Redeemed',
  description: 'James Chen redeemed $10 Off reward',
  time: '5 hours ago',
  icon: Gift,
  iconColor: '#0EA5E9'
}, {
  id: 3,
  type: 'tier',
  user: 'Emma Wilson',
  title: 'Tier Upgrade',
  description: 'Emma Wilson upgraded to Sommelier tier',
  time: '1 day ago',
  icon: Award,
  iconColor: '#6F42C1'
}, {
  id: 4,
  type: 'earned',
  user: 'David Park',
  title: 'Points Earned',
  description: 'David Park earned 100 points from Order #4523',
  time: '1 day ago',
  icon: ShoppingBag,
  iconColor: '#10B981'
}, {
  id: 5,
  type: 'earned',
  user: 'Sophie Miller',
  title: 'Points Earned',
  description: 'Sophie Miller earned 50 points from referral',
  time: '2 days ago',
  icon: User,
  iconColor: '#10B981'
}];
const topRewards = [{
  name: 'Free Shipping',
  count: 342
}, {
  name: '$10 Off Next Order',
  count: 218
}, {
  name: 'Free Tasting',
  count: 156
}, {
  name: 'Free Rosé Bottle',
  count: 89
}, {
  name: '$5 Account Credit',
  count: 47
}];
export function LoyaltyOverviewPage() {
  const navigate = useNavigate();
  const {
    isLoyaltyOnly
  } = useAppMode();
  const [searchParams] = useSearchParams();
  const [billingPreview, setBillingPreview] = useState<BillingPreviewState>('none');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [programStatus, setProgramStatus] = useState<'active' | 'paused' | 'test'>(() => searchParams.get('status') === 'test' ? 'test' : 'active');
  const [pausedSince] = useState('Mar 12, 2026');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showSwitchToTestConfirm, setShowSwitchToTestConfirm] = useState(false);
  const [testConfirmText, setTestConfirmText] = useState('');
  const [showGoLiveConfirm, setShowGoLiveConfirm] = useState(false);
  const [goLiveConfirmText, setGoLiveConfirmText] = useState('');
  // Program name (used for typed danger-zone confirmations)
  const programName = 'Acme Wine Rewards';
  // Expiration toggle (default OFF per spec)
  const [pointsExpire, setPointsExpire] = useState(false);
  const [tenureBasis, setTenureBasis] = useState<'club' | 'loyalty'>('club');
  // Klaviyo connection state (default connected for prototype)
  const [klaviyoConnected, setKlaviyoConnected] = useState(true);
  const [klaviyoEnabled, setKlaviyoEnabled] = useState(true);
  // Typed-confirm text for danger zone
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  // Toast
  const [toast, setToast] = useState<{
    id: string;
    title: string;
  } | null>(null);
  // Prototype page state toggle
  const [pageState, setPageState] = useState<'populated' | 'empty' | 'deleted' | 'firstTime'>('populated');
  // Simulate the "no program exists" auto-redirect to the setup wizard
  useEffect(() => {
    if (pageState === 'firstTime') {
      navigate('/loyalty/setup?fresh=true', {
        replace: true
      });
    }
  }, [pageState, navigate]);
  const handleStatusRadioChange = (status: 'active' | 'paused' | 'test') => {
    if (status === 'test' && programStatus !== 'test') {
      setTestConfirmText('');
      setShowSwitchToTestConfirm(true);
    } else {
      setProgramStatus(status);
      setBannerDismissed(false);
    }
  };
  const handleSaveSettings = () => {
    setToast({
      id: `t-${Date.now()}`,
      title: 'Settings saved'
    });
    setIsSettingsOpen(false);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6 relative" data-id="element-9519" data-mp="si9cspfb8pk7">
      {/* Billing status banner — this is the home screen in loyalty only mode */}
      {isLoyaltyOnly && <>
          <div className="mb-4" data-id="element-9520" data-mp="iiif6evgdufw">
            <BillingStatePreviewControl value={billingPreview} onChange={setBillingPreview} data-id="element-9521" data-mp="ilwjvtrlafqi" />
          </div>
          <div className="mb-6" data-id="element-9522" data-mp="2l8a4dv7bj81f">
            <BillingStatusBanner {...billingPreviewProps(billingPreview)} upgradePlanName="Growth" data-id="element-9523" data-mp="2gfh0tanl65qx" />
          </div>
        </>}

      {/* NavBar with breadcrumb-style title */}
      <div className="mb-6" data-id="element-9524" data-mp="26hukqkujw85d">
        <NavBar tabs={[]} title="Loyalty & Rewards › Overview" data-id="element-9525" data-mp="26im5f0mm0lgv" />
      </div>

      {/* Page actions */}
      <div className="mb-6 flex items-center justify-end gap-3" data-id="element-9526" data-mp="344a2zousx9tz">
        {/* Prototype state toggle */}
        <div className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-md p-1" data-id="element-9527" data-mp="2z7jo34uqgka5">
          <span className="text-[11px] text-[#94A3B8] pl-2 pr-1 uppercase tracking-wider" data-id="element-9528" data-mp="2u5u95e4e3mgs">
            Prototype
          </span>
          {(['populated', 'empty', 'deleted', 'firstTime'] as const).map(s => {
          const label = s === 'populated' ? 'Populated' : s === 'empty' ? 'Empty' : s === 'deleted' ? 'Just Deleted' : 'First Time';
          return <button key={s} type="button" onClick={() => setPageState(s)} className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${pageState === s ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`} data-id="element-9529" data-mp="3qdqlv8bc7h7l">
                  {label}
                </button>;
        })}
        </div>
        {pageState !== 'deleted' && <Button variant="primary" iconLeft={<Settings className="w-[18px] h-[18px]" data-id="element-9531" data-mp="8128ef0dsded" />} onClick={() => setIsSettingsOpen(true)} data-id="element-9530" data-mp="800y4cr6vuxp">
            Program Settings
          </Button>}
      </div>

      {/* Just Deleted State */}
      {pageState === 'deleted' && <div className="flex items-center justify-center py-16" data-id="element-9532" data-mp="hzrqo8w6vzv5">
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm w-full max-w-[560px] p-10 text-center" data-id="element-9533" data-mp="m0toqmede5vj">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F1F5F9] flex items-center justify-center" data-id="element-9534" data-mp="qwiux8kwal0t">
              <Trash2 className="w-8 h-8 text-[#94A3B8]" data-id="element-9535" data-mp="2nt9mhox80qa5" />
            </div>
            <h2 className="text-xl font-semibold text-[#0F172A] mb-3" data-id="element-9536" data-mp="2sq8dvzpi2j4n">
              Your loyalty program has been deleted
            </h2>
            <div className="space-y-3 text-sm text-[#475569] leading-relaxed mb-8" data-id="element-9537" data-mp="ugsq0qls5alr">
              <p className="m-0" data-id="element-9538" data-mp="ueq006kmnhh1">
                All program configuration, tiers, rewards, and bonus events have
                been removed.
              </p>
              <p className="m-0" data-id="element-9539" data-mp="139e8e5li1fa2">
                Historical points transactions and redemption records were
                preserved and remain visible from each customer's Contact Detail
                page for audit purposes.
              </p>
              <p className="m-0" data-id="element-9540" data-mp="ph6eclllo2f2">
                Wallet passes have been revoked at the provider and removed from
                customers' phones.
              </p>
            </div>
            <div className="flex items-center justify-center gap-3" data-id="element-9541" data-mp="kkpn3ij3t7xr">
              <Button variant="ghost" size="md" onClick={() => navigate('/')} data-id="element-9542" data-mp="20v1v1ys5kj8e">
                Back to Dashboard
              </Button>
              <Button variant="primary" size="md" onClick={() => setPageState('empty')} data-id="element-9543" data-mp="3dv504bw6fg24">
                Set up a new program
              </Button>
            </div>
          </div>
        </div>}

      {pageState !== 'deleted' && <>
          {/* Banners */}
          {programStatus === 'paused' && !bannerDismissed && <div className="mb-6 bg-[#FEF3C7] border-l-4 border-[#F59E0B] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm" data-id="element-9544" data-mp="39ta33jla3ou5">
              <div className="flex items-start sm:items-center gap-3" data-id="element-9545" data-mp="1fulxpsnubqg">
                <span className="text-lg" aria-hidden="true" data-id="element-9546" data-mp="5e1wg2wj6gt7">
                  ⏸️
                </span>
                <p className="text-sm text-[#92400E] font-medium m-0" data-id="element-9547" data-mp="3nst4zunhy3y0">
                  Program paused — members can still redeem but no new points
                  are being earned. Paused since {pausedSince}.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-9548" data-mp="p32k7nro8kiz">
                <button onClick={() => {
            setProgramStatus('active');
            setBannerDismissed(false);
          }} className="px-3 py-1.5 bg-white border border-[#F59E0B] text-[#92400E] text-sm font-medium rounded-md hover:bg-[#FFFBEB] transition-colors focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:ring-offset-1" data-id="element-9549" data-mp="p55h8rr8rdx7">
                  Resume Program
                </button>
                <button onClick={() => setBannerDismissed(true)} className="p-1.5 text-[#92400E] hover:bg-[#FDE68A] rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#F59E0B]" aria-label="Dismiss banner" data-id="element-9550" data-mp="19jn319i8eciv">
                  <X className="w-4 h-4" data-id="element-9551" data-mp="lspyihsrmhhn" />
                </button>
              </div>
            </div>}

          {programStatus === 'test' && !bannerDismissed && <div className="mb-6 bg-[#EDE9FE] border-l-4 border-[#8B5CF6] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm" data-id="element-9552" data-mp="qp81pd8g67rv">
              <div className="flex items-start sm:items-center gap-3" data-id="element-9553" data-mp="hpkz4o3zevx8">
                <span className="text-lg" aria-hidden="true" data-id="element-9554" data-mp="3u9p0tq4iwhvd">
                  🧪
                </span>
                <p className="text-sm text-[#5B21B6] font-medium m-0" data-id="element-9555" data-mp="3pbngl1vd2cib">
                  Test Mode active — only customers tagged 'loyalty-test'
                  participate. No Klaviyo events, Shopify tags, or wallet passes
                  will fire.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0" data-id="element-9556" data-mp="h35ejwxut1j3">
                <a href="#" className="text-sm text-[#6D28D9] font-medium hover:underline focus:outline-none" data-id="element-9557" data-mp="h1c8air5kjbm">
                  How does this work?
                </a>
                <button onClick={() => {
            setGoLiveConfirmText('');
            setShowGoLiveConfirm(true);
          }} className="px-3 py-1.5 bg-[#8B5CF6] text-white text-sm font-medium rounded-md hover:bg-[#7C3AED] transition-colors focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:ring-offset-1" data-id="element-9558" data-mp="qsys33hk3qpz">
                  Go Live
                </button>
                <button onClick={() => setBannerDismissed(true)} className="p-1.5 text-[#5B21B6] hover:bg-[#DDD6FE] rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]" aria-label="Dismiss banner" data-id="element-9559" data-mp="3onutmtmif52j">
                  <X className="w-4 h-4" data-id="element-9560" data-mp="1gsly18mo068" />
                </button>
              </div>
            </div>}

          {/* Row 1: KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-id="element-9561" data-mp="b85gx1pq36gh">
            {pageState === 'empty' ? <>
                <StatCard label="Active Members" value="0" secondaryValue="No members yet" data-id="element-9562" data-mp="g6gof5sjp4vj" />
                <StatCard label="Points Issued" value="0" secondaryValue="No points issued yet" data-id="element-9563" data-mp="l6337jo0ut6g" />
                <StatCard label="Redemption Rate" value="0%" secondaryValue="No redemptions yet" data-id="element-9564" data-mp="p87uwcuwtfqg" />
                <StatCard label="Points Liability" value="$0" secondaryValue="No liability" data-id="element-9565" data-mp="2myhkcgiqrtlt" />
              </> : <>
                <StatCard label="Active Members" value="1,247" trend={<TrendIndicator value={12} direction="up" label="vs last month" data-id="element-9567" data-mp="2jfs7ppqwcwid" />} data-id="element-9566" data-mp="2r02rrmcibfom" />
                <StatCard label="Points Issued" value="485,200" secondaryValue="this month" data-id="element-9568" data-mp="2jhmt1562yqmz" />
                <StatCard label="Redemption Rate" value="34%" trend={<TrendIndicator value={3} direction="up" data-id="element-9570" data-mp="29p77xyzhllxv" />} data-id="element-9569" data-mp="2emz26znltk58" />
                <StatCard label="Points Liability" value="$4,850" secondaryValue="unredeemed value" data-id="element-9571" data-mp="25odnxgp60ra9" />
              </>}
          </div>

          {/* Row 2: Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-9572" data-mp="32e4j7i5a6ih4">
            {/* Points Activity Chart */}
            <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm p-6" data-id="element-9573" data-mp="2xdgpuptkxpjq">
              <div className="flex items-center justify-between mb-6" data-id="element-9574" data-mp="2tg2jd5xa9vvf">
                <h3 className="text-lg font-semibold text-ocean-text-primary" data-id="element-9575" data-mp="2oe3gdx5gh0z0">
                  Points Activity
                </h3>
                <div className="flex bg-ocean-gray-100 rounded-lg p-1" data-id="element-9576" data-mp="1hwgbpohszx8m">
                  {['7d', '30d', '90d', '1y'].map((period, idx) => <button key={period} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${idx === 0 ? 'bg-white text-ocean-text-primary shadow-sm' : 'text-ocean-text-secondary hover:text-ocean-text-primary'}`} data-id="element-9577" data-mp="30wnyf47gnio8" data-mpi={idx}>
                      {period}
                    </button>)}
                </div>
              </div>
              <div className="h-[300px] w-full" data-id="element-9578" data-mp="2vy4d960oyawx">
                {pageState === 'empty' ? <div className="h-full w-full flex flex-col items-center justify-center text-center" data-id="element-9579" data-mp="3jmdwc06iskkd">
                    <p className="text-sm font-semibold text-[#475569] m-0" data-id="element-9580" data-mp="3om8k3uv00u17">
                      No points activity yet
                    </p>
                    <p className="text-xs text-[#94A3B8] mt-1 m-0" data-id="element-9581" data-mp="3aoblkswzidjw">
                      Chart will populate as customers earn and redeem
                    </p>
                  </div> : <ResponsiveContainer width="100%" height="100%" data-id="element-9582" data-mp="1z694pw0se8d4">
                    <AreaChart data={pointsActivityData} margin={{
                top: 10,
                right: 10,
                left: 0,
                bottom: 0
              }} data-id="element-9583" data-mp="246ogij5f77sz">
                      <defs data-id="element-9584" data-mp="1rnt5zeud1jbh">
                        <linearGradient id="colorEarned" x1="0" y1="0" x2="0" y2="1" data-id="element-9585" data-mp="1rmhqihrjyn4j">
                          <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.1} data-id="element-9586" data-mp="25nu2yj7i86lo" />
                          <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} data-id="element-9587" data-mp="20oh9vgq1i3an" />
                        </linearGradient>
                        <linearGradient id="colorRedeemed" x1="0" y1="0" x2="0" y2="1" data-id="element-9588" data-mp="1cxv98aid7lao">
                          <stop offset="5%" stopColor="#6F42C1" stopOpacity={0.1} data-id="element-9589" data-mp="18024llwpmzci" />
                          <stop offset="95%" stopColor="#6F42C1" stopOpacity={0} data-id="element-9590" data-mp="1mo6hg9930gnd" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-9591" data-mp="1hw5a91qrtg47" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fill: '#94A3B8',
                  fontSize: 12
                }} dy={10} data-id="element-9592" data-mp="u6kns8ves0q6" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#94A3B8',
                  fontSize: 12
                }} data-id="element-9593" data-mp="2pa8yq0etvpm9" />
                      <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-9594" data-mp="2z13jt1gc8j64" />
                      <Area type="monotone" dataKey="earned" stroke="#0EA5E9" fillOpacity={1} fill="url(#colorEarned)" strokeWidth={2} name="Earned" data-id="element-9595" data-mp="26e0qqoufl0n9" />
                      <Area type="monotone" dataKey="redeemed" stroke="#6F42C1" fillOpacity={1} fill="url(#colorRedeemed)" strokeWidth={2} name="Redeemed" data-id="element-9596" data-mp="2fddz1e6tjj6e" />
                      <Area type="monotone" dataKey="expired" stroke="#94A3B8" fill="transparent" strokeWidth={2} strokeDasharray="5 5" name="Expired" data-id="element-9597" data-mp="2kdikliml343h" />
                      <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{
                  top: -10,
                  right: 0
                }} data-id="element-9598" data-mp="3vqun9q6lxgo2" />
                    </AreaChart>
                  </ResponsiveContainer>}
              </div>
            </div>

            {/* Tier Distribution Chart */}
            <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm p-6" data-id="element-9599" data-mp="3w46q3pdz1mzo">
              <h3 className="text-lg font-semibold text-ocean-text-primary mb-6" data-id="element-9600" data-mp="3s34oj1wl0yuj">
                Tier Distribution
              </h3>
              <div className="h-[300px] w-full flex items-center justify-center" data-id="element-9601" data-mp="3n2qifpejjyz5">
                {pageState === 'empty' ? <div className="h-full w-full flex flex-col items-center justify-center text-center" data-id="element-9602" data-mp="3i82ze62b3wry">
                    <p className="text-sm font-semibold text-[#475569] m-0" data-id="element-9603" data-mp="3951v8ax04fqx">
                      No members in tiers yet
                    </p>
                    <p className="text-xs text-[#94A3B8] mt-1 m-0" data-id="element-9604" data-mp="1bdz8dww3ooq3">
                      Chart will populate once customers join your program
                    </p>
                  </div> : <ResponsiveContainer width="100%" height="100%" data-id="element-9605" data-mp="1qlx379bmtyxe">
                    <PieChart data-id="element-9606" data-mp="1qot0cvfnojks">
                      <Pie data={tierDistributionData} cx="50%" cy="50%" innerRadius={80} outerRadius={110} paddingAngle={5} dataKey="value" data-id="element-9607" data-mp="1uok0hpr2oc1z">
                        {tierDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-9608" data-mp="1lvgollw2s79g" data-mpi={index} />)}
                      </Pie>
                      <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-9609" data-mp="16ypofaekuris" />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" formatter={(value, entry: any) => <span className="text-sm text-ocean-text-secondary ml-1" data-id="element-9611" data-mp="12x4d3thoylap">
                            {value} ({entry.payload.value}%)
                          </span>} data-id="element-9610" data-mp="xzug9srz37cu" />
                    </PieChart>
                  </ResponsiveContainer>}
              </div>
            </div>
          </div>

          {/* Row 3: Activity & Rewards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-9612" data-mp="32g946ks0zeay">
            {/* Recent Activity */}
            <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm flex flex-col h-full" data-id="element-9613" data-mp="3g7np4x5htpkn">
              <div className="p-6 border-b border-ocean-border-subtle" data-id="element-9614" data-mp="3bfcofs2xhzr1">
                <h3 className="text-lg font-semibold text-ocean-text-primary" data-id="element-9615" data-mp="3q51thye56nwl">
                  Recent Activity
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto max-h-[400px] p-6" data-id="element-9616" data-mp="3l5p9dlennuxp">
                {pageState === 'empty' ? <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center px-6" data-id="element-9617" data-mp="302gi3ccy0bc">
                    <div className="w-14 h-14 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4" data-id="element-9618" data-mp="3v2a9ptfqchv7">
                      <BarChart3 className="w-7 h-7 text-[#94A3B8]" data-id="element-9619" data-mp="20qdfxtttezwz" />
                    </div>
                    <p className="text-sm font-semibold text-[#0F172A] m-0 mb-1" data-id="element-9620" data-mp="20sq7mypdkon8">
                      No activity yet
                    </p>
                    <p className="text-xs text-[#64748B] max-w-[280px] m-0" data-id="element-9621" data-mp="134alp3zv5fym">
                      When customers earn or redeem points, their activity will
                      show up here.
                    </p>
                  </div> : <ol className="list-none space-y-0" role="list" aria-label="Recent loyalty activity" data-id="element-9622" data-mp="1cvncrtdu823l">
                    {recentActivity.map((activity, index) => <TimelineEvent key={activity.id} title={activity.title} description={activity.description} date={activity.time} icon={activity.icon} iconColor={activity.iconColor} isLast={index === recentActivity.length - 1} data-id="element-9623" data-mp="k6pwfvt6sfdh" data-mpi={index} />)}
                  </ol>}
              </div>
              <div className="p-4 border-t border-ocean-border-subtle bg-ocean-gray-50 rounded-b-lg" data-id="element-9624" data-mp="u6qezfj8ergm">
                <Button variant="ghost" size="sm" onClick={() => navigate('/loyalty/members')} iconRight={<ChevronRight className="w-4 h-4" data-id="element-9626" data-mp="25lvn4z24ti7m" />} data-id="element-9625" data-mp="29n8sl81bbje0">
                  View All Members
                </Button>
              </div>
            </div>

            {/* Top Rewards */}
            <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm flex flex-col h-full" data-id="element-9627" data-mp="2i3znwimo8dse">
              <div className="p-6 border-b border-ocean-border-subtle" data-id="element-9628" data-mp="2378oi9czz22s">
                <h3 className="text-lg font-semibold text-ocean-text-primary" data-id="element-9629" data-mp="2850gt4hk0q7i">
                  Top Rewards
                </h3>
              </div>
              <div className="flex-1 p-6" data-id="element-9630" data-mp="30swh0j6c1091">
                {pageState === 'empty' ? <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center px-6" data-id="element-9631" data-mp="30s4qhmrldspm">
                    <div className="w-14 h-14 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4" data-id="element-9632" data-mp="2m25pzdcke56z">
                      <Gift className="w-7 h-7 text-[#94A3B8]" data-id="element-9633" data-mp="3esxkm6dzels7" />
                    </div>
                    <p className="text-sm font-semibold text-[#0F172A] m-0 mb-1" data-id="element-9634" data-mp="3jjdx9o9za3di">
                      No rewards yet
                    </p>
                    <p className="text-xs text-[#64748B] max-w-[300px] m-0 mb-4" data-id="element-9635" data-mp="3tebfy7avlp6y">
                      Add rewards so members have something to earn points
                      toward.
                    </p>
                    <Button variant="primary" size="sm" onClick={() => navigate('/loyalty/rewards')} iconRight={<ChevronRight className="w-4 h-4" data-id="element-9637" data-mp="3fejwvs5i5im3" />} data-id="element-9636" data-mp="3kdd6h9ho4pgz">
                      Create Your First Reward
                    </Button>
                  </div> : <div className="space-y-3" data-id="element-9638" data-mp="3aicyckffr9e6">
                    {topRewards.map((reward, index) => {
                const maxCount = topRewards[0].count;
                const barPercent = Math.round(reward.count / maxCount * 100);
                const rankColors = ['bg-[#F59E0B] text-white', 'bg-[#94A3B8] text-white', 'bg-[#CD7F32] text-white'];
                const rankClass = index < 3 ? rankColors[index] : 'bg-ocean-gray-100 text-ocean-text-secondary';
                return <div key={index} className="group relative rounded-lg border border-ocean-border-subtle hover:border-ocean-primary/30 hover:shadow-sm transition-all p-3" data-id="element-9639" data-mp="2mrfxc2p6hwhp" data-mpi={index}>
                          <div className="flex items-center gap-3 mb-2" data-id="element-9640" data-mp="176x6344bvsk7">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${rankClass}`} data-id="element-9641" data-mp="1c7cintgscw14">
                              {index + 1}
                            </div>
                            <span className="text-sm font-medium text-ocean-text-primary flex-1 truncate" data-id="element-9642" data-mp="jjglknnb123t">
                              {reward.name}
                            </span>
                            <span className="text-sm font-semibold text-ocean-text-primary tabular-nums" data-id="element-9643" data-mp="sitvtij95upl">
                              {reward.count.toLocaleString()}
                            </span>
                          </div>
                          <div className="ml-9" data-id="element-9644" data-mp="rearsxcgg1h">
                            <div className="h-1.5 bg-ocean-gray-100 rounded-full overflow-hidden" data-id="element-9645" data-mp="4szlp8qbik91">
                              <div className="h-full rounded-full bg-gradient-to-r from-ocean-primary to-[#38BDF8] transition-all duration-500" style={{
                        width: `${barPercent}%`
                      }} data-id="element-9646" data-mp="3htqy28y7x84u" />
                            </div>
                            <div className="flex items-center justify-between mt-1" data-id="element-9647" data-mp="3rm6f941oxsoo">
                              <span className="text-[11px] text-ocean-text-muted" data-id="element-9648" data-mp="3ms28aksfdknl">
                                redemptions
                              </span>
                              <span className="text-[11px] text-ocean-text-muted" data-id="element-9649" data-mp="3vmgjab5xa90w">
                                {barPercent}% of top
                              </span>
                            </div>
                          </div>
                        </div>;
              })}
                  </div>}
              </div>
              <div className="p-4 border-t border-ocean-border-subtle bg-ocean-gray-50 rounded-b-lg" data-id="element-9650" data-mp="2g5pzf9m85a1a">
                <Button variant="ghost" size="sm" onClick={() => navigate('/loyalty/rewards')} iconRight={<ChevronRight className="w-4 h-4" data-id="element-9652" data-mp="qp44v1usd1f9" />} data-id="element-9651" data-mp="2b7elonst6fds">
                  Manage Rewards
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Program Settings Drawer Overlay */}
      {isSettingsOpen && <div className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300" onClick={() => setIsSettingsOpen(false)} data-id="element-9653" data-mp="gw5epfjn52tj" />}

      {/* Program Settings Drawer Panel */}
      <div className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${isSettingsOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-9654" data-mp="7vqivm91w5bs">
        {/* Drawer Header */}
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between flex-shrink-0" data-id="element-9655" data-mp="2xyngi97612u">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-9656" data-mp="1scn3ctgzy15m">
            Program Settings
          </h2>
          <IconButton icon={<X className="w-5 h-5" data-id="element-9658" data-mp="1xawy52yt6zqp" />} label="Close settings" variant="ghost" onClick={() => setIsSettingsOpen(false)} data-id="element-9657" data-mp="1xchtuvwv0oq5" />
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8" data-id="element-9659" data-mp="1qw9axzsv2s13">
          {/* Section 1: Program Basics */}
          <section data-id="element-9660" data-mp="1lw3co0s367a1">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-9661" data-mp="1lxo8dtq4zw9h">
              Program Basics
            </h3>
            <div className="space-y-4" data-id="element-9662" data-mp="1h0644gn2e57t">
              <Input type="text" label="Program Name" defaultValue="Acme Wine Rewards" data-id="element-9663" data-mp="2ekiokefx89h5" />

              <div data-id="element-9664" data-mp="29m7ec2lprwye">
                <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-9665" data-mp="29nsa1vjrllxu">
                  Program Status
                </label>
                <div className="flex gap-4" data-id="element-9666" data-mp="24te9leq8womb">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-9667" data-mp="1zvmj8p591ak8">
                    <input type="radio" name="status" checked={programStatus === 'active'} onChange={() => handleStatusRadioChange('active')} className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-9668" data-mp="2xbs2czwq5kej" />
                    <span className="text-sm text-[#0F172A]" data-id="element-9669" data-mp="30u8xtmleyq0z">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-9670" data-mp="34vazea2sze64">
                    <input type="radio" name="status" checked={programStatus === 'paused'} onChange={() => handleStatusRadioChange('paused')} className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-9671" data-mp="34xe0pq9eotq5" />
                    <span className="text-sm text-[#0F172A]" data-id="element-9672" data-mp="3erntva669w1x">Paused</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-9673" data-mp="3jqqxcq6i7u7s">
                    <input type="radio" name="status" checked={programStatus === 'test'} onChange={() => handleStatusRadioChange('test')} className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-9674" data-mp="3nsvp1jde6grs" />
                    <span className="text-sm text-[#0F172A]" data-id="element-9675" data-mp="1dj2ch9t8412">Test Mode</span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Audience Mode */}
          <section data-id="element-9676" data-mp="bd9w2gqjqf03">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-9677" data-mp="banoih3nnzhv">
              Audience Mode
            </h3>
            {isLoyaltyOnly && <p className="text-xs text-[#94A3B8] -mt-2 mb-4" data-id="element-9678" data-mp="ro3yso04ocq4">
                Audience settings apply when wine club subscriptions are on.
                Switch to "Wine club + loyalty" in Settings to use them.
              </p>}
            <div className={`space-y-3 transition-opacity ${isLoyaltyOnly ? 'opacity-50' : ''}`} data-id="element-9679" data-mp="10nhdbv33zrhp">
              <label className={`flex items-start gap-3 p-3 border border-[#E2E8F0] rounded-lg transition-colors ${isLoyaltyOnly ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-[#F8FAFC]'}`} data-id="element-9680" data-mp="cxwm6owsxnxc">
                <input type="radio" name="audience" defaultChecked disabled={isLoyaltyOnly} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9] disabled:cursor-not-allowed" data-id="element-9681" data-mp="mrvpvscixfre" />
                <div data-id="element-9682" data-mp="3qdb5jp9fbrwe">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-9683" data-mp="3qcsyryp4xva8">
                    All Customers
                  </div>
                  <div className="text-xs text-[#64748B]" data-id="element-9684" data-mp="20y0f5ifcet20">
                    Both subscribers and non-subscribers earn and redeem
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border border-[#E2E8F0] rounded-lg transition-colors ${isLoyaltyOnly ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-[#F8FAFC]'}`} data-id="element-9685" data-mp="1rxc3uvhc36st">
                <input type="radio" name="audience" disabled={isLoyaltyOnly} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9] disabled:cursor-not-allowed" data-id="element-9686" data-mp="2km0ug0krjtqb" />
                <div data-id="element-9687" data-mp="2omjps4lv7hx9">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-9688" data-mp="2ol0767cqkusb">
                    Non-Subscribers Only
                  </div>
                  <div className="text-xs text-[#64748B]" data-id="element-9689" data-mp="2flvbd8sk7fju">
                    Only non-subscribers participate
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border border-[#E2E8F0] rounded-lg transition-colors ${isLoyaltyOnly ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-[#F8FAFC]'}`} data-id="element-9690" data-mp="1477zglwbn30r">
                <input type="radio" name="audience" disabled={isLoyaltyOnly} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9] disabled:cursor-not-allowed" data-id="element-9691" data-mp="1hng527j19beu" />
                <div data-id="element-9692" data-mp="1mn2mkt5w9dtg">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-9693" data-mp="1mkor4j7axzld">
                    Custom (Tag-Based)
                  </div>
                  <div className="text-xs text-[#64748B]" data-id="element-9694" data-mp="tydpd57ylsmz">
                    Limit to customers with specific tags
                  </div>
                </div>
              </label>
            </div>
          </section>

          {/* Section 3: Points Economy */}
          <section data-id="element-9695" data-mp="13u714mduti3p">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-9696" data-mp="18mi1fqcib5ws">
              Points Economy
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-9697" data-mp="b6kybgcgahhw">
              <div data-id="element-9698" data-mp="g67admgjlrp4">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9699" data-mp="g5nqi07c0t8g">
                  Earning Rate
                </label>
                <div className="relative" data-id="element-9700" data-mp="2tfxli5s2h0ou">
                  <input type="number" defaultValue="10" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-9701" data-mp="2pfeuuf1wu0o8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-9702" data-mp="2ffwavym02ew6">
                    pts/$1
                  </span>
                </div>
              </div>
              <div data-id="element-9703" data-mp="25qw6u0sn4tb4">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9704" data-mp="25qct6wa0wr2w">
                  Redemption Value
                </label>
                <div className="relative" data-id="element-9705" data-mp="1wqg9l92hqiep">
                  <input type="number" defaultValue="0.01" step="0.01" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-9706" data-mp="1wrj19j7n0ovo" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-9707" data-mp="1mud6o0ztefqp">
                    $/pt
                  </span>
                </div>
              </div>
              <div data-id="element-9708" data-mp="1i52b4zaauyvq">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9709" data-mp="143zpgnn7vqlc">
                  Minimum Redemption
                </label>
                <div className="relative" data-id="element-9710" data-mp="186ml6zn0dvnt">
                  <input type="number" defaultValue="500" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-9711" data-mp="187ej4h42w5h9" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-9712" data-mp="1n33ullbn107q">
                    points
                  </span>
                </div>
              </div>
              <div data-id="element-9713" data-mp="2eua1cklv5w5z">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9714" data-mp="2etrvcw9eg3kv">
                  Rounding
                </label>
                <div className="relative" data-id="element-9715" data-mp="2jmbcg37apguf">
                  <select className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-9716" data-mp="85hs5ifsb9vv">
                    <option data-id="element-9717" data-mp="hvucb9jot11h">Nearest</option>
                    <option data-id="element-9718" data-mp="hxniyouavl9h">Floor</option>
                    <option data-id="element-9719" data-mp="cvy6r5vcd1jt">Ceiling</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-9720" data-mp="cyu5gwf0ju9b" />
                </div>
              </div>
            </div>
          </section>

          {/* Section 3b: Membership Tenure */}
          <section data-id="element-9721" data-mp="3vb8qlvhgceto">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-9722" data-mp="3vbs8xdb0l588">
              Membership Tenure
            </h3>
            <span className="block text-xs font-medium text-[#475569] mb-2" data-id="element-9723" data-mp="1ajzz01uax03y">
              Count tenure from
            </span>
            <div className="space-y-3" data-id="element-9724" data-mp="15k3ttz9vb2mt">
              {[{
              id: 'club' as const,
              label: 'Club start date',
              help: "The date the member's first subscription started, including subscriptions brought across in a migration. Customers without a subscription use their loyalty join date."
            }, {
              id: 'loyalty' as const,
              label: 'Loyalty join date',
              help: 'The date the member joined the loyalty program.'
            }].map(option => <label key={option.id} className="flex items-start gap-2 cursor-pointer" data-id="element-9725" data-mp="3u7ifg37xdem2">
                  <input type="radio" name="tenureBasis" checked={tenureBasis === option.id} onChange={() => setTenureBasis(option.id)} className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-9726" data-mp="30yvnyk42ebm" />
                  <span data-id="element-9727" data-mp="bukbzmbxc3n5">
                    <span className="block text-sm text-[#0F172A]" data-id="element-9728" data-mp="btsqj4c1jmal">
                      {option.label}
                    </span>
                    <span className="block text-xs text-[#64748B]" data-id="element-9729" data-mp="grkhnw4v34dq">
                      {option.help}
                    </span>
                  </span>
                </label>)}
            </div>
            <p className="text-xs text-[#64748B] mt-3" data-id="element-9730" data-mp="vu2dv2odu6pb">
              Used by Anniversary Rewards and the Minimum tenure rule on
              rewards.
            </p>
          </section>

          {/* Section 4: Expiration */}
          <section data-id="element-9731" data-mp="hx5luxwj2j6u">
            <div className="flex items-center justify-between mb-4" data-id="element-9732" data-mp="hxp5qk5qnhni">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9733" data-mp="1emn1o5p85lm9">
                Expiration
              </h3>
              <div className="flex items-center gap-2" data-id="element-9734" data-mp="19p4p85c00o9i">
                <span className="text-sm text-[#0F172A]" data-id="element-9735" data-mp="14wv6pad7qqh9">Points Expire</span>
                <button type="button" onClick={() => setPointsExpire(!pointsExpire)} aria-pressed={pointsExpire} className={`w-11 h-6 rounded-full relative transition-colors ${pointsExpire ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-9736" data-mp="14uqqbsy4nz03">
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${pointsExpire ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-9737" data-mp="1sm5y418ob8iq" />
                </button>
              </div>
            </div>
            <div className={`space-y-4 transition-opacity ${pointsExpire ? 'opacity-100' : 'opacity-50'}`} data-id="element-9738" data-mp="3rsbbxfrqld05">
              {/* Inactivity-based expiry */}
              <div data-id="element-9739" data-mp="3i3jlxd68l2rv">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9740" data-mp="3i31fxotrva6r">
                  Expire after
                </label>
                <div className="flex flex-wrap items-center gap-2" data-id="element-9741" data-mp="342qq8p4781n6">
                  <div className="relative w-28" data-id="element-9742" data-mp="39468yaiwlw2e">
                    <input type="number" min="1" defaultValue="12" disabled={!pointsExpire} className="w-full h-10 pl-3 pr-16 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none disabled:bg-[#F8FAFC] disabled:cursor-not-allowed" data-id="element-9743" data-mp="390sa3c9g84zp" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-9744" data-mp="xdc3tjkge2hr">
                      months
                    </span>
                  </div>
                  <span className="text-sm text-[#0F172A]" data-id="element-9745" data-mp="2hta5f043rk6x">of no activity</span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-1.5" data-id="element-9746" data-mp="2cup5l1srj2j0">
                  Activity means earning or redeeming points, so this only
                  affects members who have gone quiet.
                </p>
              </div>

              {/* Warning reminder */}
              <div data-id="element-9747" data-mp="30kjjlmotix4u">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9748" data-mp="30nyyopihs669">
                  Expiry Warning
                </label>
                <div className="relative w-28" data-id="element-9749" data-mp="3akk3pauihha3">
                  <input type="number" min="1" defaultValue="30" disabled={!pointsExpire} className="w-full h-10 pl-3 pr-12 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none disabled:bg-[#F8FAFC] disabled:cursor-not-allowed" data-id="element-9750" data-mp="3ai7np38bdgzo" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-9751" data-mp="1b9j14lpl43m2">
                    days
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-1.5" data-id="element-9752" data-mp="ubiswiamybxi">
                  Sends a reminder email this many days before a member's points
                  are due to expire.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5: Klaviyo Integration */}
          <section data-id="element-9753" data-mp="6gzbuy7rpghc">
            <div className="flex items-center justify-between mb-4" data-id="element-9754" data-mp="6hixaowmmn04">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9755" data-mp="berdwl24miz9">
                Klaviyo Integration
              </h3>
              {klaviyoConnected && <button type="button" onClick={() => setKlaviyoEnabled(!klaviyoEnabled)} aria-pressed={klaviyoEnabled} className={`w-11 h-6 rounded-full relative transition-colors ${klaviyoEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-9756" data-mp="lcp0z58929qa">
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${klaviyoEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-9757" data-mp="1wpra5f80rx0f" />
                </button>}
            </div>

            {klaviyoConnected ? <div className="space-y-3" data-id="element-9758" data-mp="37jjuux8pjs1n">
                {['Points earned', 'Points redeemed', 'Tier changed', 'Reward redeemed', 'Points expiring', 'Birthday bonus'].map(event => <div key={event} className="block" data-id="element-9759" data-mp="32imemq79d82s">
                    <Checkbox checked={true} onChange={() => {}} label={event} data-id="element-9760" data-mp="2xlvxdzjpw6fs" />
                  </div>)}
              </div> : <div className="border border-[#E2E8F0] rounded-lg p-4 bg-[#F8FAFC]" data-id="element-9761" data-mp="2tizes8u6gabu">
                <div className="flex items-start gap-3 mb-3" data-id="element-9762" data-mp="2ok5y67jlg37g">
                  <div className="w-10 h-10 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center flex-shrink-0" data-id="element-9763" data-mp="2omscqr4wjiz6">
                    <Zap className="w-5 h-5 text-[#0EA5E9]" data-id="element-9764" data-mp="mtueugw9q07d" />
                  </div>
                  <div className="flex-1" data-id="element-9765" data-mp="htzsmqnftysn">
                    <h4 className="text-sm font-semibold text-[#0F172A] mb-1" data-id="element-9766" data-mp="hvau2jteuugl">
                      Connect Klaviyo to send loyalty events
                    </h4>
                    <p className="text-xs text-[#64748B] leading-relaxed" data-id="element-9767" data-mp="zw0my9b23mob">
                      When connected, WinePik will send events for points
                      earned, redeemed, tier changes, and more.
                    </p>
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={() => navigate('/integrations')} iconRight={<ChevronRight className="w-4 h-4" data-id="element-9769" data-mp="1jk9jswnjogu5" />} data-id="element-9768" data-mp="1nlmk6r7vbrto">
                  Go to Integrations
                </Button>
              </div>}

            {/* Dev-only toggle to flip connected state for prototyping */}
            <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-[#94A3B8]" data-id="element-9770" data-mp="19uznf4lo4c08">
              <span data-id="element-9771" data-mp="27adkav5fs0gx">Prototype: Klaviyo connected</span>
              <button type="button" onClick={() => setKlaviyoConnected(!klaviyoConnected)} aria-pressed={klaviyoConnected} className={`w-8 h-4 rounded-full relative transition-colors ${klaviyoConnected ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-9772" data-mp="27b6ktedsxtqm">
                <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-transform ${klaviyoConnected ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-9773" data-mp="2esuwj94p5tdl" />
              </button>
            </div>
          </section>

          {/* Section 6: Danger Zone */}
          <section className="border border-[#FECACA] rounded-lg p-4 bg-[#FEF2F2]" data-id="element-9774" data-mp="2st5oyglnng0v">
            <h3 className="text-sm font-semibold text-[#DC2626] uppercase tracking-wider mb-4" data-id="element-9775" data-mp="2sr1mm33emp2p">
              Danger Zone
            </h3>
            <div className="space-y-3" data-id="element-9776" data-mp="32ifje33z1as5">
              <Button variant="secondary" size="sm" onClick={() => {
              setResetConfirmText('');
              setShowResetConfirm(true);
            }} className="w-full border-[#FCA5A5] text-[#DC2626] hover:bg-[#FEE2E2]" data-id="element-9777" data-mp="32jh28tr8b1kf">
                Reset All Points
              </Button>
              <Button variant="danger" size="sm" onClick={() => {
              setDeleteConfirmText('');
              setShowDeleteConfirm(true);
            }} className="w-full" data-id="element-9778" data-mp="3gj9rekn3x9qa">
                Delete Program
              </Button>
            </div>
          </section>
        </div>

        {/* Drawer Footer */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-white flex items-center justify-end gap-3 flex-shrink-0" data-id="element-9779" data-mp="1q7lg0kfuibq9">
          <Button variant="ghost" size="md" onClick={() => setIsSettingsOpen(false)} data-id="element-9780" data-mp="204gams8zzrz9">
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSaveSettings} data-id="element-9781" data-mp="1v5wnioio4a5b">
            Save Settings
          </Button>
        </div>
      </div>

      {/* Reset All Points Confirmation Modal */}
      {showResetConfirm && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowResetConfirm(false)} data-id="element-9782" data-mp="247cusne6rw5b" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-9783" data-mp="ern8il0bfxr5">
            <div className="bg-white rounded-lg shadow-xl w-[480px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-9784" data-mp="9tbzgchuhr9z">
              <div className="p-6" data-id="element-9785" data-mp="2lg5pdsxvo0se">
                <div className="flex items-start gap-4" data-id="element-9786" data-mp="2qe7askis0c71">
                  <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0" data-id="element-9787" data-mp="2qdp3msuksdkc">
                    <AlertTriangle className="w-5 h-5 text-[#F59E0B]" data-id="element-9788" data-mp="2cl980d407htg" />
                  </div>
                  <div className="flex-1" data-id="element-9789" data-mp="2gmksk480tcus">
                    <h3 className="text-base font-semibold text-[#0F172A] mb-2" data-id="element-9790" data-mp="22omnxqji4ush">
                      Reset all points across your loyalty program
                    </h3>
                    <p className="text-sm text-[#475569] leading-relaxed mb-4" data-id="element-9791" data-mp="1to7svzgqk1bs">
                      This will set every member's point balance to zero. A
                      program_reset transaction will be written for each member
                      so the change is recoverable via the activity log, but the
                      points themselves cannot be restored.
                    </p>
                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 mb-4" data-id="element-9792" data-mp="33cwmz7i1ukuk">
                      <ul className="text-xs text-[#475569] space-y-1.5 m-0 list-none" data-id="element-9793" data-mp="2ye3ig4kqq46j">
                        <li className="flex items-center gap-2" data-id="element-9794" data-mp="2yc8zuwwxyo5m">
                          <span className="w-1 h-1 rounded-full bg-[#94A3B8]" data-id="element-9795" data-mp="2tdfkeyy3gn2t" />
                          <span data-id="element-9796" data-mp="2oevxav7rl58v">
                            <span className="font-semibold text-[#0F172A]" data-id="element-9797" data-mp="2oi07c97wuv7g">
                              1,247
                            </span>{' '}
                            members affected
                          </span>
                        </li>
                        <li className="flex items-center gap-2" data-id="element-9798" data-mp="2anpmi2z1bl6o">
                          <span className="w-1 h-1 rounded-full bg-[#94A3B8]" data-id="element-9799" data-mp="25pfvm4kcf6qv" />
                          <span data-id="element-9800" data-mp="25nbhyuwn6tde">
                            <span className="font-semibold text-[#0F172A]" data-id="element-9801" data-mp="20qurvv5vu4xo">
                              485,200
                            </span>{' '}
                            total points wiped
                          </span>
                        </li>
                        <li className="flex items-center gap-2" data-id="element-9802" data-mp="c8ryzmj9c2p1">
                          <span className="w-1 h-1 rounded-full bg-[#94A3B8]" data-id="element-9803" data-mp="g6zeyf5wczz6" />
                          <span data-id="element-9804" data-mp="l73x09mge2ri">
                            Tier evaluations triggered for all members
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="space-y-2" data-id="element-9805" data-mp="3op5e79dt8puc">
                      <label className="block text-sm font-medium text-[#0F172A]" data-id="element-9806" data-mp="3om9fvjy1vz5d">
                        Type the program name to confirm:
                      </label>
                      <div className="text-sm font-mono text-[#475569] bg-[#F1F5F9] border border-[#E2E8F0] rounded-md px-3 py-1.5 select-all" data-id="element-9807" data-mp="2fkvk782uwrl">
                        {programName}
                      </div>
                      <input type="text" value={resetConfirmText} onChange={e => setResetConfirmText(e.target.value)} placeholder={programName} className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626] focus:outline-none" data-id="element-9808" data-mp="35vi82mtpogt3" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-3" data-id="element-9809" data-mp="3clb4ryyf9srr">
                <Button variant="ghost" size="sm" onClick={() => setShowResetConfirm(false)} data-id="element-9810" data-mp="37ot1l3iqq2hj">
                  Cancel
                </Button>
                <Button variant="danger" size="sm" disabled={resetConfirmText !== programName} onClick={() => {
              setShowResetConfirm(false);
              setResetConfirmText('');
            }} data-id="element-9811" data-mp="2fw25ct124dsa">
                  Reset All Points
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Delete Program Confirmation Modal */}
      {showDeleteConfirm && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowDeleteConfirm(false)} data-id="element-9812" data-mp="44y3zwqyxgti" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-9813" data-mp="1m2l3aj83510t">
            <div className="bg-white rounded-lg shadow-xl w-[500px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-9814" data-mp="1r2z7trah9su3">
              <div className="p-6" data-id="element-9815" data-mp="1d4irr9qq6pza">
                <div className="flex items-start gap-4" data-id="element-9816" data-mp="1d5alef0ridmx">
                  <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center flex-shrink-0" data-id="element-9817" data-mp="1i2u2rnsrb6j3">
                    <AlertTriangle className="w-5 h-5 text-[#EF4444]" data-id="element-9818" data-mp="25sev2xasvg51" />
                  </div>
                  <div className="flex-1" data-id="element-9819" data-mp="29sf9wj1t9tcm">
                    <h3 className="text-base font-semibold text-[#0F172A] mb-2" data-id="element-9820" data-mp="1w226mtzv72tm">
                      ⚠ Delete loyalty program
                    </h3>
                    <p className="text-sm text-[#475569] leading-relaxed mb-3" data-id="element-9821" data-mp="20z2bj1kz9zj5">
                      The following will be permanently deleted:
                    </p>
                    <ul className="text-sm text-[#475569] space-y-1.5 mb-4 list-disc pl-5" data-id="element-9822" data-mp="2ti98ph2y18qc">
                      <li data-id="element-9823" data-mp="tf80eav10udf">The entire loyalty program configuration</li>
                      <li data-id="element-9824" data-mp="oo83h5lwqcax">
                        <span className="font-semibold text-[#0F172A]" data-id="element-9825" data-mp="onyhru7warb1">
                          1,247
                        </span>{' '}
                        member records
                      </li>
                      <li data-id="element-9826" data-mp="er3fr1cey8s0">
                        <span className="font-semibold text-[#0F172A]" data-id="element-9827" data-mp="ervegl1b4mmi">12</span>{' '}
                        active tier upgrades
                      </li>
                      <li data-id="element-9828" data-mp="17eg4ni61qsoh">
                        <span className="font-semibold text-[#0F172A]" data-id="element-9829" data-mp="17g0w2yx2dvi4">4</span>{' '}
                        reward catalog items
                      </li>
                      <li data-id="element-9830" data-mp="yeb6zzqnn8ni">All transaction history</li>
                      <li data-id="element-9831" data-mp="ydrpul8twoaj">All wallet passes (will be revoked at provider)</li>
                    </ul>
                    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-md p-3 mb-4" data-id="element-9832" data-mp="3n76pdcn367iz">
                      <p className="text-xs text-[#991B1B] m-0 font-medium" data-id="element-9833" data-mp="3j2yyb8t5ofhl">
                        This action cannot be undone. Member balances will not
                        be recoverable.
                      </p>
                    </div>
                    <div className="space-y-2" data-id="element-9834" data-mp="27n607rbkxc1b">
                      <label className="block text-sm font-medium text-[#0F172A]" data-id="element-9835" data-mp="27lcrm6heohqo">
                        Type the program name to confirm:
                      </label>
                      <div className="text-sm font-mono text-[#475569] bg-[#F1F5F9] border border-[#E2E8F0] rounded-md px-3 py-1.5 select-all" data-id="element-9836" data-mp="2hbnyo1wdz71s">
                        {programName}
                      </div>
                      <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder={programName} className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626] focus:outline-none" data-id="element-9837" data-mp="2qep4s2l94y5g" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-3" data-id="element-9838" data-mp="mjzghruudak3">
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)} data-id="element-9839" data-mp="rbrakcd7hyez">
                  Cancel
                </Button>
                <Button variant="danger" size="sm" disabled={deleteConfirmText !== programName} onClick={() => {
              setShowDeleteConfirm(false);
              setDeleteConfirmText('');
              setIsSettingsOpen(false);
            }} data-id="element-9840" data-mp="hl63e0jgvlkg">
                  Delete Program
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Switch to Test Mode Modal */}
      {showSwitchToTestConfirm && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowSwitchToTestConfirm(false)} data-id="element-9841" data-mp="10acotvbnar9y" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-9842" data-mp="3gyqdo1pkbptx">
            <div className="bg-white rounded-lg shadow-xl w-[460px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-9843" data-mp="3l2o9o3jlckir">
              <div className="p-6" data-id="element-9844" data-mp="3dgj5uvyg4fa0">
                <div className="flex items-start gap-4" data-id="element-9845" data-mp="38jj6f3w3qarv">
                  <div className="w-10 h-10 rounded-full bg-[#EDE9FE] flex items-center justify-center flex-shrink-0" data-id="element-9846" data-mp="38j14prqo74ck">
                    <AlertTriangle className="w-5 h-5 text-[#8B5CF6]" data-id="element-9847" data-mp="3ne79ychxyz4u" />
                  </div>
                  <div className="flex-1" data-id="element-9848" data-mp="3ifma4e6lqhgx">
                    <h3 className="text-base font-semibold text-[#0F172A] mb-2" data-id="element-9849" data-mp="2ur33s4744erh">
                      Switch to Test Mode?
                    </h3>
                    <p className="text-sm text-[#475569] mb-3" data-id="element-9850" data-mp="2pqotsglyauqt">
                      Here's what will change when Test Mode is active:
                    </p>
                    <ul className="text-sm text-[#475569] space-y-2 mb-4 list-disc pl-4" data-id="element-9851" data-mp="2zpxqvic44phe">
                      <li data-id="element-9852" data-mp="2c0cxe5ic29tv">
                        Customers without the 'loyalty-test' tag will stop
                        earning points (balances preserved)
                      </li>
                      <li data-id="element-9853" data-mp="2wr8rdyg7dyp3">
                        Klaviyo events, Shopify tags, and wallet passes will not
                        fire
                      </li>
                      <li data-id="element-9854" data-mp="30pobnukwgsyt">
                        When going Live again, test customer balances WILL be
                        reset to zero
                      </li>
                    </ul>
                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 mb-4" data-id="element-9855" data-mp="3ajommd2v4sfg">
                      <p className="text-xs text-[#64748B] m-0" data-id="element-9856" data-mp="2dto42vt3786l">
                        <span className="font-medium text-[#475569]" data-id="element-9857" data-mp="2hty7q1tepmia">
                          Note:
                        </span>{' '}
                        Most teams use Pause instead if they need to make
                        changes before continuing.
                      </p>
                    </div>
                    <div className="space-y-2" data-id="element-9858" data-mp="21itm0ljlysq">
                      <label className="block text-sm font-medium text-[#0F172A]" data-id="element-9859" data-mp="70vfai0ogztq">
                        Type SWITCH to confirm
                      </label>
                      <input type="text" value={testConfirmText} onChange={e => setTestConfirmText(e.target.value)} className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] focus:outline-none" placeholder="SWITCH" data-id="element-9860" data-mp="1riedruu46cjx" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-3" data-id="element-9861" data-mp="18tg05dj7blyr">
                <Button variant="ghost" size="sm" onClick={() => setShowSwitchToTestConfirm(false)} data-id="element-9862" data-mp="13sa8lj1a24qt">
                  Cancel
                </Button>
                <button disabled={testConfirmText !== 'SWITCH'} onClick={() => {
              setProgramStatus('test');
              setBannerDismissed(false);
              setShowSwitchToTestConfirm(false);
            }} className="px-4 py-2 bg-[#8B5CF6] text-white text-sm font-medium rounded-md hover:bg-[#7C3AED] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:ring-offset-1" data-id="element-9863" data-mp="tvxckei9fesw">
                  Switch to Test
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Go Live Modal */}
      {showGoLiveConfirm && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowGoLiveConfirm(false)} data-id="element-9864" data-mp="2r96vvgrdrd9v" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-9865" data-mp="2dibs2ivqnmd5">
            <div className="bg-white rounded-lg shadow-xl w-[460px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-9866" data-mp="2ibwjfs2tti3d">
              <div className="p-6" data-id="element-9867" data-mp="23jcnbbe3gavf">
                <div className="flex items-start gap-4" data-id="element-9868" data-mp="28k0lqi4njtzo">
                  <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0" data-id="element-9869" data-mp="28ifsqwxzkj3x">
                    <CheckCircle2 className="w-5 h-5 text-[#0EA5E9]" data-id="element-9870" data-mp="1ymcn3h3xe2c8" />
                  </div>
                  <div className="flex-1" data-id="element-9871" data-mp="1fy28dn9o2ysh">
                    <h3 className="text-base font-semibold text-[#0F172A] mb-2" data-id="element-9872" data-mp="1fz3i9oey5f8m">
                      Go Live with your Loyalty Program
                    </h3>
                    <p className="text-sm text-[#475569] mb-3" data-id="element-9873" data-mp="1oyi8qnv45pt4">
                      Test Mode is about to end. Here's what will happen:
                    </p>
                    <ul className="text-sm text-[#475569] space-y-2 mb-4" data-id="element-9874" data-mp="1jycb8r25x934">
                      <li className="flex items-start gap-2" data-id="element-9875" data-mp="1yvmxmvgcfzdm">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" data-id="element-9876" data-mp="1ty36iuyjrs49" />
                        <span data-id="element-9877" data-mp="27ozz4lsh49bb">
                          All customers will start participating in loyalty
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-9878" data-mp="23og01cocavmj">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" data-id="element-9879" data-mp="2hoh2uxo60d4n" />
                        <span data-id="element-9880" data-mp="2cp46nmbeltpe">Klaviyo events will begin firing</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-9881" data-mp="1abpsgunt1fau">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" data-id="element-9882" data-mp="1a93lox8qn3to" />
                        <span data-id="element-9883" data-mp="1ea5lbf6khhw6">
                          Shopify customer tags will be applied on tier changes
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-9884" data-mp="1o3w8mgqyjmaa">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" data-id="element-9885" data-mp="1t1xrp1odvllr" />
                        <span data-id="element-9886" data-mp="1xzzjqc3sev9d">Wallet passes (if enabled) will be created</span>
                      </li>
                    </ul>
                    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-md p-3 mb-4 flex items-start gap-2" data-id="element-9887" data-mp="223e0j4zjvfnv">
                      <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 flex-shrink-0" data-id="element-9888" data-mp="270e6zh0bakfi" />
                      <p className="text-xs text-[#991B1B] m-0" data-id="element-9889" data-mp="7pwb1cfl48q7">
                        <span className="font-semibold" data-id="element-9890" data-mp="cmd14c6cgx5x">
                          Test customer balances will be reset to zero.
                        </span>{' '}
                        You have 7 test customers with combined 4,250 points.
                      </p>
                    </div>
                    <div className="space-y-2" data-id="element-9891" data-mp="3rigdnitlj76j">
                      <label className="block text-sm font-medium text-[#0F172A]" data-id="element-9892" data-mp="3rh59hhw8nxew">
                        Type GO LIVE to confirm
                      </label>
                      <input type="text" value={goLiveConfirmText} onChange={e => setGoLiveConfirmText(e.target.value)} className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" placeholder="GO LIVE" data-id="element-9893" data-mp="o1ga8van3kp4" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-3" data-id="element-9894" data-mp="2yv2dlbulpvi7">
                <Button variant="ghost" size="sm" onClick={() => setShowGoLiveConfirm(false)} data-id="element-9895" data-mp="2urw8i7j86adb">
                  Cancel
                </Button>
                <Button variant="primary" size="sm" disabled={goLiveConfirmText !== 'GO LIVE'} onClick={() => {
              setProgramStatus('active');
              setBannerDismissed(false);
              setShowGoLiveConfirm(false);
            }} data-id="element-9896" data-mp="xufjuonzby95">
                  Go Live
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Toast */}
      {toast && <Toast id={toast.id} type="success" title={toast.title} duration={4000} onClose={() => setToast(null)} data-id="element-9897" data-mp="1hiew63y6zdgo" />}
    </div>;
}